'use client'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'
import { paths } from '@router/paths'
import { Fund, isUpcomingOrComingSoonStage } from '@domain/funds/model'
import { FundsListPlaceholder } from './FundsListPlaceholder'
import { CarouselItem } from '@components/ui/carousel'
import Link from 'next/link'
import { PreIpoFundListItem } from './PreIpoFundListItem'
import { Button } from '@components/ui/button'
import { PreIpoFundComingCard } from './PreIpoFundComingCard'
import type { HomePageFundsClientPayload } from '@domain/landing/homePageFundPayload'
import { usePreIpoLowestInAppValuations } from '@domain/pre-ipo-access/api/usePreIpoLowestInAppValuations'

// Lazy-load Splide carousel — only needed on mobile, keeps ~30-50 KB out of initial JS
const LazyFundListCarousel = dynamic(() =>
  import('./FundListCarousel').then((m) => ({ default: m.FundListCarousel })),
)

export function FundsPreviewList({
  homeFunds,
  fundsData,
}: {
  homeFunds?: HomePageFundsClientPayload
  fundsData?: Fund[]
}) {
  const allFundAttributes = useMemo(() => homeFunds?.data ?? [], [homeFunds])
  const { data: lowestInAppValuations } =
    usePreIpoLowestInAppValuations(allFundAttributes)

  if (!homeFunds) {
    return <FundsListPlaceholder />
  }

  const promotedStrapiFunds = homeFunds.data.filter((fund) => fund.promoteOnMainPage)

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between relative z-0 items-start sm:items-center mb-10">
        <h2
          className={`text-5xl sm:text-7xl mt-[72px] sm:mt-0 font-funnel text-cw-midnight-purple font-medium`}
        >
          Pre-IPO Opportunities
        </h2>
        <Link
          href={paths.funds.exclusiveAccess.companies}
          className="relative sm:ml-auto mt-10 sm:mt-0 ml-0 z-10 font-medium text-sm uppercase tracking-wide pointer-events-auto inline-block"
        >
          <Button variant="secondaryDark">See More Opportunities</Button>
        </Link>
      </div>
      <div className="block sm:hidden">
        <LazyFundListCarousel>
          {!!promotedStrapiFunds.length && (
            <>
              {promotedStrapiFunds.map((fund) => (
                <CarouselItem key={fund.documentId} className="px-1 py-1">
                  {isUpcomingOrComingSoonStage(fund.stage) ? (
                    <PreIpoFundComingCard
                      key={fund.documentId}
                      fundMetadata={fund}
                      variant="dark"
                      stage={fund.stage ?? undefined}
                    />
                  ) : (
                    <PreIpoFundListItem
                      fund={fundsData?.find((f) => f.address === fund.fundAddress)!}
                      fundMetadata={fund}
                      variant="dark"
                      lowestInAppValuation={lowestInAppValuations?.[fund.fundUrlId]}
                    />
                  )}
                </CarouselItem>
              ))}
            </>
          )}
        </LazyFundListCarousel>
      </div>
      <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 gap-4">
        {promotedStrapiFunds.map((fund) => {
          return isUpcomingOrComingSoonStage(fund.stage) ? (
            <PreIpoFundComingCard
              key={fund.documentId}
              fundMetadata={fund}
              variant="dark"
              stage={fund.stage ?? undefined}
            />
          ) : (
            <PreIpoFundListItem
              key={fund.documentId}
              fund={fundsData?.find((f) => f.address === fund.fundAddress)!}
              fundMetadata={fund}
              variant="dark"
            />
          )
        })}
      </div>
    </>
  )
}
